/**
 * Hooks for the crew-mobile surface — the Today list and a single task's
 * detail (wireframe 08).
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CrewScheduleResponse,
  CrewTaskDetailResponse,
  CrewTaskNoteCreate,
  CrewTaskTimelineNote,
  CrewTodayResponse,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { enqueueMutation, isNetworkError, newIdempotencyKey } from '@/lib/offlineQueue';

/** Shared query options so a prefetch warms the exact entry the hook reads.
 *  `crewMemberId` (supervisors/admins only) views another crew member's day. */
export function crewTodayOptions(date?: string, crewMemberId?: string) {
  const qs = new URLSearchParams();
  if (date) qs.set('date', date);
  if (crewMemberId) qs.set('crew_member_id', crewMemberId);
  const query = qs.toString();
  return queryOptions({
    queryKey: ['crew-today', date ?? 'today', crewMemberId ?? 'me'],
    queryFn: () =>
      apiCall<CrewTodayResponse>(`/crew/me/today${query ? `?${query}` : ''}`),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useCrewToday(date?: string, crewMemberId?: string) {
  return useQuery(crewTodayOptions(date, crewMemberId));
}

/** Shared query options so a prefetch warms the exact entry the hook reads.
 *  `crewMemberId` (supervisors/admins only) views another crew member's week. */
export function crewScheduleOptions(params?: {
  from?: string;
  to?: string;
  crewMemberId?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.crewMemberId) qs.set('crew_member_id', params.crewMemberId);
  const query = qs.toString();
  return queryOptions({
    queryKey: [
      'crew-schedule',
      params?.from ?? '',
      params?.to ?? '',
      params?.crewMemberId ?? 'me',
    ],
    queryFn: () =>
      apiCall<CrewScheduleResponse>(
        `/crew/me/schedule${query ? `?${query}` : ''}`,
      ),
    staleTime: 30_000,
  });
}

export function useCrewSchedule(params?: {
  from?: string;
  to?: string;
  crewMemberId?: string;
}) {
  return useQuery(crewScheduleOptions(params));
}

export function useCrewTaskDetail(taskId: string | undefined) {
  return useQuery<CrewTaskDetailResponse>({
    queryKey: ['crew-task-detail', taskId],
    enabled: !!taskId,
    queryFn: () => apiCall<CrewTaskDetailResponse>(`/crew/me/tasks/${taskId}`),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

/** A note accepted into the offline queue instead of sent (B.1). */
export interface QueuedNote {
  queued: true;
}
export type AddNoteResult = CrewTaskTimelineNote | QueuedNote;

export function isQueuedNote(r: AddNoteResult): r is QueuedNote {
  return typeof r === 'object' && r !== null && 'queued' in r && r.queued === true;
}

export function useAddTaskNote(taskId: string) {
  const qc = useQueryClient();
  return useMutation<AddNoteResult, Error, CrewTaskNoteCreate>({
    mutationFn: async (body) => {
      const key = newIdempotencyKey();
      try {
        return await apiCall<CrewTaskTimelineNote>(`/crew/me/tasks/${taskId}/notes`, {
          method: 'POST',
          body,
          headers: { 'Idempotency-Key': key },
        });
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        // Field notes are capture — don't lose them to a dead zone (B.1).
        await enqueueMutation({
          path: `/crew/me/tasks/${taskId}/notes`,
          method: 'POST',
          body,
          idempotencyKey: key,
          label: `Note · ${body.body.slice(0, 40)}${body.body.length > 40 ? '…' : ''}`,
        });
        return { queued: true };
      }
    },
    onSuccess: (res) => {
      if (isQueuedNote(res)) return;
      qc.invalidateQueries({ queryKey: ['crew-task-detail', taskId] });
    },
  });
}
